// SelectedTemplateForm.tsx
import React from "react"
import { List, Input, Checkbox } from "antd"
import parse from "html-react-parser"
import { Loader } from "shared/ui/Loader/Loader.component"
import { TemplateSection, TemplateInput } from "types/template.types"

// Define the props interface for SelectedTemplateForm
interface SelectedTemplateFormProps {
  /** A list of exceptions that can be used in the template */
  exceptions: unknown[]
  /** Flag indicating if the template is being parsed */
  parsingSelectedTemplate: boolean
  /** The current section of the template being rendered */
  section: TemplateSection
  /** The current input values for the template fields */
  values: Record<string, string>
  /** Callback function to handle changes in input values */
  onChange: (label: string, newValue: string) => void
}

/**
 * SelectedTemplateForm component renders a form for inputting data into a selected template section.
 * It handles both text inputs and list-based inputs with exception handling.
 *
 * @param props - The component props
 * @returns The rendered SelectedTemplateForm component
 */
const SelectedTemplateForm: React.FC<SelectedTemplateFormProps> = ({
  exceptions,
  parsingSelectedTemplate,
  section,
  values,
  onChange,
}) => {
  if (parsingSelectedTemplate) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader fontSize={24} color="#36C3ED" />
        Parsing template...
      </div>
    )
  }

  /**
   * Handles the change in input values for a specific field.
   *
   * @function handleInputChange
   * @param label - The label of the input field
   * @param newValue - The new value for the input field
   */
  const handleInputChange = (label: string, newValue: string): void => {
    onChange(label, newValue)
  }

  /**
   * Handles the change in exception selections for a specific field.
   *
   * @function handleExceptionChange
   * @param label - The label of the input field
   * @param exception - The exception to be toggled
   */
  const handleExceptionChange = (label: string, exception: string): void => {
    const currentValue = values[label] || ""
    const lines = currentValue
      .split("\n")
      .filter((line: string) => line.trim() !== "")
    if (lines.includes(exception)) {
      onChange(
        label,
        lines.filter((line: string) => line !== exception).join("\n")
      )
    } else {
      onChange(label, [...lines, exception].join("\n") + "\n")
    }
  }

  /**
   * Checks if an exception is currently selected for a specific field.
   *
   * @function isExceptionChecked
   * @param label - The label of the input field
   * @param exception - The exception to check
   * @returns True if the exception is selected, false otherwise
   */
  const isExceptionChecked = (label: string, exception: string): boolean => {
    const currentValue = values[label] || ""
    return currentValue.split("\n").includes(exception)
  }

  /**
   * Renders the inputs for the current template section.
   *
   * @function renderInputs
   * @returns The rendered inputs for the template section
   */
  const renderInputs = () => {
    return section.inputs.map((input: TemplateInput, index: number) => {
      const placeholder = `{{${input.label}}}`
      const renderedTemplate = section.show.replace(
        new RegExp(placeholder, "g"),
        (values[input.label] || "").replace(/\n/g, "<br />")
      )

      return (
        <div key={index} className="flex flex-col mt-5">
          <div className="flex flex-row mb-5">
            <div className="flex flex-col mr-5 space-y-2 w-full">
              <p className="text-md">
                <strong>Instruction:</strong> {input.instruction}
              </p>
              {input.type === "TEXT" && (
                <Input.TextArea
                  size="large"
                  rows={5}
                  style={{
                    resize: "none",
                    borderRadius: "8px",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleInputChange(input.label, e.target.value)
                  }
                  value={values[input.label] || ""}
                />
              )}
              {input.type === "LIST" && (
                <Input.TextArea
                  size="large"
                  rows={5}
                  style={{
                    resize: "none",
                    borderRadius: "8px",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleInputChange(input.label, e.target.value)
                  }
                  value={values[input.label] || ""}
                />
              )}
            </div>
            {input.exceptions && (
              <div className="flex w-full flex-col">
                <p className="text-xl">Exceptions to use</p>
                <List
                  className="flex flex-col max-h-96 overflow-y-auto"
                  itemLayout="horizontal"
                  dataSource={exceptions}
                  renderItem={(item: unknown, index: number) => (
                    <List.Item
                      key={index}
                      className="flex cursor-pointer shadow-sm hover:bg-slate-100"
                      onClick={() =>
                        handleExceptionChange(input.label, String(item))
                      }
                      actions={[
                        <Checkbox
                          key={index}
                          className="mr-2"
                          onChange={() =>
                            handleExceptionChange(input.label, String(item))
                          }
                          checked={isExceptionChecked(
                            input.label,
                            String(item)
                          )}
                        />,
                      ]}
                    >
                      <List.Item.Meta
                        className="p-2 text-left font-semibold "
                        title={String(item)}
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
          <div className="flex flex-row">
            {section.show && (
              <div className="flex flex-col w-full overflow-auto h-52">
                {parse(renderedTemplate)}
              </div>
            )}
          </div>
        </div>
      )
    })
  }

  return <>{renderInputs()}</>
}

export default SelectedTemplateForm
